/**
 * AddContactNoteField — note form-field type `add_contacts` (Jun 2026).
 * Renders an "Add Contact" button inside a note; submitting the shared
 * ContactFormDialog POSTs to the client's chart contacts AND stamps a
 * snapshot of the added contact into this field's note value so the
 * note permanently shows who was added during that session.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import { Label } from '../../../ui/label';
import { Button } from '../../../ui/button';
import { ContactFormDialog } from '../../../features/ContactFormDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: '', relationship: '', title_agency: '', phone: '', fax: '', email: '',
  can_receive_info: false, release_on_file: false, is_minor_or_legal_guardian: false,
  release_id: '', end_date: '', discharge_date: '', auto_calculate_end_date: false,
  lives_in_same_household: false,
  contact_address: '', contact_city: '', contact_state: '', contact_zipcode: '',
};

const REL_LABELS = { pcp: 'PCP (Primary Care)', other_provider: 'Other Provider' };
const relLabel = (r) => REL_LABELS[r] || (r || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const parseAddedContacts = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch (e) { return []; }
  }
  return [];
};

export const AddContactNoteField = ({ field, fieldStyle, clientId, value, onAppend, isViewMode, disabled }) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY);
  const contacts = parseAddedContacts(value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId) { toast.error('No client is linked to this note.'); return; }
    if (!formData.name || !formData.relationship) {
      toast.error('Please fill in Name and Relationship.');
      return;
    }
    try {
      const payload = {
        ...formData,
        email: formData.email || null,
        phone: formData.phone || null,
        discharge_date: null,
        end_date: null,
        release_id: null,
      };
      const r = await axios.post(`${API}/clients/${clientId}/contacts`, payload);
      const saved = r.data || {};
      onAppend({
        contact_id: saved.id || null,
        client_id: clientId,
        name: formData.name,
        relationship: formData.relationship,
        title_agency: formData.title_agency || '',
        phone: formData.phone || '',
        fax: formData.fax || '',
        email: formData.email || '',
        address: [formData.contact_address, formData.contact_city, formData.contact_state, formData.contact_zipcode]
          .filter(Boolean).join(', '),
        added_at: new Date().toISOString(),
      });
      window.dispatchEvent(new CustomEvent('contacts:changed', { detail: { clientId } }));
      toast.success(`${formData.name} added to the client's contacts`);
      setFormData(EMPTY);
      setOpen(false);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d)
        ? d.map((x) => x.msg || x.message || 'Validation error').join(', ')
        : (typeof d === 'string' ? d : 'Failed to add contact'));
    }
  };

  return (
    <div style={fieldStyle} data-testid={`add-contacts-field-${field.id}`}>
      <div className="flex items-center justify-between gap-2">
        <Label>{field.label || 'Contacts Added This Session'}</Label>
        {!isViewMode && !disabled && (
          <Button
            type="button" size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => { setFormData(EMPTY); setOpen(true); }}
            data-testid={`add-contact-from-note-btn-${field.id}`}
          >
            <UserPlus className="h-3 w-3 mr-1" /> Add Contact
          </Button>
        )}
      </div>
      {contacts.length === 0 ? (
        <p className="text-xs text-gray-400 italic mt-1">
          {isViewMode
            ? 'No contacts were added on this note.'
            : "Contacts added here are saved to the client's Contacts tab and listed on this note."}
        </p>
      ) : (
        <div className="mt-1 space-y-1">
          {contacts.map((c, i) => (
            <div
              key={c.contact_id || i}
              className="border rounded px-2 py-1.5 bg-teal-50/50 border-teal-200 text-xs"
              data-testid={`note-added-contact-${i}`}
            >
              <span className="font-medium">{c.name}</span>
              <span className="text-gray-500"> — {relLabel(c.relationship)}</span>
              {c.title_agency && <span className="text-gray-500"> · {c.title_agency}</span>}
              {(c.phone || c.fax || c.email || c.address) && (
                <div className="text-gray-600 mt-0.5">
                  {[c.phone && `Ph: ${c.phone}`, c.fax && `Fax: ${c.fax}`, c.email, c.address]
                    .filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {open && (
        <ContactFormDialog
          open={open}
          onOpenChange={setOpen}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={() => setOpen(false)}
        />
      )}
    </div>
  );
};

export default AddContactNoteField;
